/**
 * GuidelineMarkdown — themed read-only renderer for guideline markdown.
 *
 * Ported from digital_sizer's GuidelineMarkdownReader and refined for the
 * Eastview reading surface: a measured, document-like column with anchored
 * headings (ids are assigned by the parent screen for scrollspy), GFM tables,
 * and a graceful placeholder for the digital-sizer `bound-table` fence (whose
 * live data binding is a backend feature we don't wire here).
 */
import Box from "@mui/material/Box";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";

const READER_SX = {
  color: "#1f2933",
  fontFamily: "Inter, sans-serif",
  fontSize: 15,
  lineHeight: 1.75,
  "& > :first-of-type": { mt: 0 },
  "& h1": {
    fontSize: 30,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    mt: 0,
    mb: 2,
    pb: 1.25,
    borderBottom: "2px solid",
    borderColor: "#1F5AF2",
    color: "#0f172a",
    scrollMarginTop: 24,
  },
  "& h2": {
    fontSize: 22,
    fontWeight: 700,
    mt: 4.5,
    mb: 1.5,
    pb: 0.5,
    borderBottom: "1px solid #e5e7eb",
    color: "#0f172a",
    scrollMarginTop: 24,
  },
  "& h3": {
    fontSize: 17,
    fontWeight: 700,
    mt: 3,
    mb: 1,
    color: "#1F5AF2",
    scrollMarginTop: 24,
  },
  "& h4": { fontSize: 15.5, fontWeight: 700, mt: 2.5, mb: 0.75, color: "#0f172a" },
  "& h5, & h6": { fontSize: 14, fontWeight: 700, mt: 2, mb: 0.5, color: "#334155" },
  "& p": { my: 1.25 },
  "& ul, & ol": { pl: 3.25, my: 1.25 },
  "& li": { my: 0.5 },
  "& li > p": { my: 0.4 },
  "& li::marker": { color: "#64748b" },
  "& strong": { fontWeight: 700, color: "#0f172a" },
  "& em": { fontStyle: "italic" },
  "& a": {
    color: "#1F5AF2",
    textDecoration: "none",
    fontWeight: 600,
    "&:hover": { textDecoration: "underline" },
  },
  "& blockquote": {
    borderLeft: "4px solid #1F5AF2",
    bgcolor: "#f1f6f9",
    pl: 2,
    pr: 1.5,
    py: 0.75,
    my: 1.75,
    borderRadius: "0 6px 6px 0",
    color: "#334155",
    "& p": { my: 0.5 },
  },
  "& code": {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "0.85em",
    bgcolor: "#eef2f5",
    color: "#0f172a",
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
  },
  "& pre": {
    bgcolor: "#0f172a",
    color: "#e2e8f0",
    p: 2,
    borderRadius: 1.5,
    overflowX: "auto",
    my: 1.75,
    fontSize: 13,
    "& code": { bgcolor: "transparent", color: "inherit", p: 0 },
  },
  "& hr": { border: 0, borderTop: "1px solid #e5e7eb", my: 3.5 },
  "& table": {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    my: 2,
    fontSize: 13.5,
    border: "1px solid #e5e7eb",
    borderRadius: 1.5,
    overflow: "hidden",
  },
  "& th, & td": {
    borderBottom: "1px solid #eef2f5",
    borderRight: "1px solid #eef2f5",
    px: 1.5,
    py: 1,
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th:last-of-type, & td:last-of-type": { borderRight: "none" },
  "& tr:last-of-type td": { borderBottom: "none" },
  "& th": {
    bgcolor: "#f1f6f9",
    fontWeight: 700,
    color: "#0f172a",
    whiteSpace: "nowrap",
  },
  "& tbody tr:hover": { bgcolor: "#f8fafc" },
  "& img": { maxWidth: "100%", height: "auto", borderRadius: 1 },
  "& input[type=checkbox]": { mr: 1 },
};

function BoundTablePlaceholder() {
  return (
    <Flx
      ac
      g={1}
      sx={{
        my: 2,
        px: 2,
        py: 1.5,
        border: "1px dashed #cbd5e1",
        borderRadius: 1.5,
        bgcolor: "#f8fafc",
        color: "#64748b",
      }}
    >
      <TableChartOutlined sx={{ fontSize: 20 }} />
      <Txt sx={{ fontSize: 13 }}>
        Embedded rate table — view in the full guideline editor.
      </Txt>
    </Flx>
  );
}

const MARKDOWN_COMPONENTS = {
  code({ className, children, ...props }) {
    const lang = /language-([\w-]+)/.exec(className || "")?.[1];
    if (lang === "bound-table") return <BoundTablePlaceholder />;
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

export default function GuidelineMarkdown({ markdown }) {
  return (
    <Box sx={READER_SX}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {markdown || ""}
      </ReactMarkdown>
    </Box>
  );
}
